import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Search, Plus, Trash2, Loader2, Library, Pencil, Save } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50 transition-colors";

const DIFF_BADGE = {
  easy: "border-moss/40 bg-moss/10 text-green-700",
  intermediate: "border-cadmium/40 bg-cadmium/10 text-sky-600",
  hard: "border-amber/50 bg-amber/10 text-amber-700",
};

const EditForm = ({ q, onSaved, onCancel }) => {
  const [form, setForm] = useState({ q: q.q, options: [...q.options], answer: q.answer, category: q.category, difficulty: q.difficulty });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/training/questions/${q.id}`, form);
      toast.success("Bank question updated");
      onSaved(res.data);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 space-y-2 border-t border-slate-200 pt-3" data-testid={`qb-edit-form-${q.id}`}>
      <input className={inputCls} value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} data-testid={`qb-edit-q-${q.id}`} />
      {form.options.map((o, oi) => (
        <label key={oi} className="flex items-center gap-2">
          <input type="radio" name={`qb-edit-${q.id}`} checked={form.answer === oi} onChange={() => setForm({ ...form, answer: oi })} className="accent-[#2E9CFF]" data-testid={`qb-edit-answer-${q.id}-${oi}`} />
          <input className={inputCls} value={o} onChange={(e) => setForm({ ...form, options: form.options.map((x, j) => (j === oi ? e.target.value : x)) })} data-testid={`qb-edit-opt-${q.id}-${oi}`} />
        </label>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${inputCls} !w-40`} placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid={`qb-edit-cat-${q.id}`} />
        <select className={`${inputCls} !w-36`} value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} data-testid={`qb-edit-diff-${q.id}`}>
          <option value="easy">Easy</option>
          <option value="intermediate">Intermediate</option>
          <option value="hard">Hard</option>
        </select>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-cadmium px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60" data-testid={`qb-edit-save-${q.id}`}>
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
        </button>
        <button onClick={onCancel} className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-slate-900 transition-colors" data-testid={`qb-edit-cancel-${q.id}`}>Cancel</button>
      </div>
    </div>
  );
};

export const QuestionBankPicker = ({ onInsert, onClose }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("");
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/training/questions", { params: { search, category: cat } });
      setQuestions(res.data.questions || []);
    } catch {
      toast.error("Could not load question bank");
    } finally {
      setLoading(false);
    }
  }, [search, cat]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const categories = useMemo(() => [...new Set(questions.map((q) => q.category))].sort(), [questions]);

  const remove = async (q) => {
    if (!window.confirm(`Delete this bank question?\n\n"${q.q}"`)) return;
    try {
      await api.delete(`/training/questions/${q.id}`);
      toast.success("Bank question deleted");
      setQuestions((qs) => qs.filter((x) => x.id !== q.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="question-bank-picker" onClick={onClose}>
      <div className="technical-card w-full max-w-2xl max-h-[85vh] flex flex-col p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Library size={14} className="text-sky-600" />
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Question Bank</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-slate-900 transition-colors" aria-label="Close" data-testid="qb-close"><X size={16} /></button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-4">Reusable exam questions. Insert one into this exam, or edit / delete entries.</p>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
            <input className={`${inputCls} !pl-8`} placeholder="Search questions or categories…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="qb-search" />
          </div>
          <select className={`${inputCls} !w-44`} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="qb-cat-filter">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading && <div className="flex items-center justify-center py-10"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>}
          {!loading && questions.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="qb-empty">No bank questions yet — use "Save to bank" on any exam question.</p>
          )}
          {!loading && questions.map((q) => (
            <div key={q.id} className="rounded-md border border-slate-200 bg-slate-50 p-4" data-testid={`qb-row-${q.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">{q.q}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{q.category}</span>
                    <span className={`rounded-sm border px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider ${DIFF_BADGE[q.difficulty] || DIFF_BADGE.intermediate}`}>{q.difficulty}</span>
                    <span className="text-[10px] font-mono text-muted-foreground/60">{q.options.length} options · used {q.times_used}×</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => onInsert(q)} className="inline-flex items-center gap-1 rounded-md bg-cadmium px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors" data-testid={`qb-insert-${q.id}`}>
                    <Plus size={11} /> Insert
                  </button>
                  <button onClick={() => setEditingId(editingId === q.id ? null : q.id)} className="text-muted-foreground hover:text-sky-600 transition-colors" aria-label="Edit question" data-testid={`qb-edit-${q.id}`}><Pencil size={13} /></button>
                  <button onClick={() => remove(q)} className="text-muted-foreground hover:text-red-600 transition-colors" aria-label="Delete question" data-testid={`qb-delete-${q.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
              {editingId === q.id && (
                <EditForm q={q} onSaved={(updated) => { setQuestions((qs) => qs.map((x) => (x.id === q.id ? updated : x))); setEditingId(null); }} onCancel={() => setEditingId(null)} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
